import { Span, Loading, Button } from '@pandiafi/design-system'
import { Formik, Field, FieldProps, Form } from 'formik'
import { t } from 'i18next'
import { DateTime } from 'luxon'
import React, { useEffect } from 'react'
import { Trans } from 'react-i18next'
import { createSearchParams, useNavigate } from 'react-router-dom'
import { Text } from '@pandiafi/components'

import { PinCodeTextInput } from './PinCodeTextInput'
import { ResendVerificationCodeErrorSection } from './ResendVerificationCodeErrorSection'
import { ResendVerificationCodeSuccessSection } from './ResendVerificationCodeSuccessSection'
import {
  SetVerificationCodeRequest,
  setVerificationCodeInputSchema,
} from '../../../common/validations'
import routePath from '../../router/routePath'
import { TextLink } from '../Password/Forgot/TextLink'
import { PageTranslationSafeLabel } from '../../components'

import { useAPIResendVerificationCode } from 'api/apiHooks/useAPIResendVerificationCode'
import { useAPISetVerificationCode } from 'api/apiHooks/useAPISetVerificationCode'
import { FormErrorSection } from 'views/Login/FormErrorSection'
import { validateFormValues } from 'utils'
import FormFieldWrapper from 'components/FormField'

type VerifyCodeMode = 'registration' | 'returnRegistration'

export const VerifyCodeForm: React.FC<{
  email: string
  /**
   * Verification code is set successfully
   */
  onSuccess: () => void
  /**
   * User pressed cancel link from the view
   */
  onCancel: () => void
  /**
   * Hook for resend verification code action
   */
  onResendCode?: () => void
  /**
   * When this component mounts on the first time, send verification code
   */
  sendVerificationCodeOnMount?: boolean
  /**
   * With this flag it's possible to determine the texts based on the mode
   * where the verification code form is integrated to
   */
  mode?: VerifyCodeMode
}> = ({
  email,
  mode = 'registration',
  onSuccess,
  onCancel,
  onResendCode = () => {},
  sendVerificationCodeOnMount,
}) => {
  const navigate = useNavigate()
  const [resendTime, setResendTime] = React.useState('')
  const [isSentVerificationCodeOnMount, setIsSentVerificationCodeOnMount] =
    React.useState(false)

  const { mutate, isError, isPending } = useAPISetVerificationCode({
    onSuccess: () => onSuccess(),
  })

  const {
    mutate: mutateResendVerificationCode,
    isSuccess: isSuccessResendCode,
    isError: isErrorResendCode,
    isPending: isPendingResendCode,
  } = useAPIResendVerificationCode()

  const now = DateTime.now().toFormat('HH:mm')

  // Resend verification code only once on first render if requested
  // Normally verification code is sent automatically on the new user creation
  useEffect(() => {
    if (sendVerificationCodeOnMount && !isSentVerificationCodeOnMount) {
      mutateResendVerificationCode({ email })
      setResendTime(now)
      setIsSentVerificationCodeOnMount(true)
    }
  }, [
    sendVerificationCodeOnMount,
    mutateResendVerificationCode,
    isSentVerificationCodeOnMount,
    now,
    email,
  ])

  const handleValidate = (formValues: SetVerificationCodeRequest) => {
    const validationErrors = validateFormValues(
      formValues,
      setVerificationCodeInputSchema
    )

    return validationErrors
  }

  const handleSubmit = (values: SetVerificationCodeRequest) => {
    mutate(values)
  }

  const resendLink = (
    <TextLink
      text={t('registration:verificationCodeResendLink')}
      onClick={() => {
        try {
          setResendTime('')
          mutateResendVerificationCode({ email })
          setResendTime(now)
          onResendCode()
        } catch {
          setResendTime('')
        }
      }}
    />
  )

  const changeEmailLinkSection = (
    <Span light>
      {t('registration:continueRegistrationInfo4')}{' '}
      <TextLink
        text={t('registration:resetRegistrationLinkText')}
        onClick={() => {
          navigate({
            pathname: routePath.registerAuth,
            search: createSearchParams({
              resetOldUser: 'true',
            }).toString(),
          })
        }}
      />
      .
    </Span>
  )

  return (
    <Formik
      initialValues={{
        verificationCode: '',
        email,
      }}
      onSubmit={handleSubmit}
      validate={handleValidate}
      validateOnMount
    >
      {({ isValid }) => (
        <>
          {mode === 'registration' && (
            <div className="flex flex-col gap-4 pb-5">
              <Text size="l" weight="medium" element="h3">
                {t('registration:confirmEmailHeader')}
              </Text>
              <Span light>
                {t('registration:verificationCodeDescription')}{' '}
                <strong>{email}</strong>.
              </Span>
              <Span light>
                <>{t('registration:verificationCodeResendDescription')} </>
                {resendLink}.
              </Span>
              {changeEmailLinkSection}
            </div>
          )}
          {mode === 'returnRegistration' && (
            <div className="flex flex-col gap-4 pb-5">
              <Span light>{t('registration:continueRegistrationInfo1')}</Span>
              <Span light>
                <Trans
                  values={{
                    email,
                  }}
                  t={t}
                  i18nKey="registration:continueRegistrationInfo2"
                  components={{
                    strong: <strong />,
                  }}
                />
              </Span>
              <Span light>
                {t('registration:continueRegistrationInfo3')} {resendLink}.
              </Span>
              {changeEmailLinkSection}
            </div>
          )}

          {isPendingResendCode && <Loading size="small" />}

          {!isPendingResendCode &&
            !isErrorResendCode &&
            isSuccessResendCode && (
              <ResendVerificationCodeSuccessSection resendTime={resendTime} />
            )}

          {isErrorResendCode && <ResendVerificationCodeErrorSection />}

          <Form className="flex flex-col gap-5">
            <div className="flex flex-col items-center">
              <Field name="verificationCode">
                {({
                  field,
                  meta,
                }: FieldProps<
                  SetVerificationCodeRequest['verificationCode']
                >) => (
                  <FormFieldWrapper
                    className="flex flex-col items-center"
                    name={field.name}
                    meta={meta}
                    label={t('registration:verificationCodeLabel')}
                  >
                    <PinCodeTextInput
                      {...field}
                      disabled={isPending}
                      name="verificationCode"
                    />
                  </FormFieldWrapper>
                )}
              </Field>
            </div>

            {isError && (
              <FormErrorSection
                error={t('registration:verificationCodeError')}
              />
            )}

            <Button
              loading={isPending}
              disabled={!isValid || isPending}
              type="submit"
            >
              <PageTranslationSafeLabel>
                {t('registration:verificationCodeSubmit')}
              </PageTranslationSafeLabel>
            </Button>

            <Button onClick={onCancel} type="button" variant="cancel">
              <PageTranslationSafeLabel>
                {t('common:cancel')}
              </PageTranslationSafeLabel>
            </Button>
          </Form>
        </>
      )}
    </Formik>
  )
}
